import { format as formatLocalDate } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

const LAB_TIME_ZONES = {
  EST: "America/New_York",
  CST: "America/Chicago",
  MST: "America/Denver",
  PST: "America/Los_Angeles",
  HST: "Pacific/Honolulu",
} as const;

type LabTimeZoneKey = keyof typeof LAB_TIME_ZONES;
type RequisitionRow = Record<string, unknown>;

const TIME_ZONE_COLUMNS = new Set([
  "dateofcollection",
  "timeofcollection",
  "collectiondate",
  "receiveddate",
  "addeddate",
  "validatedate",
  "validateddate",
  "publisheddate",
  "publishdate",
  "ordereddate",
  "datereceived",
  "createddate",
]);

const getRowValue = (
  rowData: RequisitionRow | null | undefined,
  columnKey: string
) => {
  if (!rowData || !columnKey) return undefined;

  const matchingKey = Object.keys(rowData).find(
    (key) => key.toLowerCase() === columnKey.toLowerCase()
  );

  return matchingKey ? rowData[matchingKey] : undefined;
};

const getLabTimeZone = (value?: string | null) => {
  const key = value?.trim().toUpperCase() as LabTimeZoneKey | undefined;

  if (!key || !LAB_TIME_ZONES[key]) return null;

  return {
    key,
    timeZone: LAB_TIME_ZONES[key],
  };
};

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const getLabDateTimeParts = (
  dateValue: unknown,
  timeValue?: unknown
): string | null => {
  if (typeof dateValue !== "string" || !dateValue.trim()) return null;

  const value = dateValue.trim();
  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](.+))?/);
  const usDateMatch = value.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](.+))?/
  );

  const year = isoDateMatch?.[1] ?? usDateMatch?.[3];
  const month = isoDateMatch?.[2] ?? usDateMatch?.[1];
  const day = isoDateMatch?.[3] ?? usDateMatch?.[2];
  const embeddedTime = isoDateMatch?.[4] ?? usDateMatch?.[4];

  if (!year || !month || !day) return null;

  const calendarDate = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day))
  );
  if (
    calendarDate.getUTCFullYear() !== Number(year) ||
    calendarDate.getUTCMonth() !== Number(month) - 1 ||
    calendarDate.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  const rawTime =
    typeof timeValue === "string" && timeValue.trim()
      ? timeValue.trim()
      : (embeddedTime ?? "00:00:00");
  const timeMatch = rawTime.match(
    /^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?\s*(AM|PM)?/i
  );

  if (!timeMatch) return null;

  let hour = Number(timeMatch[1]);
  const period = timeMatch[4]?.toUpperCase();

  if (period) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (period === "PM" ? 12 : 0);
  } else if (hour > 23) {
    return null;
  }

  return `${year}-${month.padStart(2, "0")}-${day.padStart(
    2,
    "0"
  )}T${String(hour).padStart(2, "0")}:${timeMatch[2]}:${timeMatch[3] ?? "00"}`;
};

/** Converts a date/time entered in the lab's timezone into a UTC instant. */
export const convertLabDateTimeToUtc = (
  dateValue: unknown,
  timeValue: unknown,
  labTimeZone?: string | null
): Date | null => {
  const zone = getLabTimeZone(labTimeZone);
  const labDateTime = getLabDateTimeParts(dateValue, timeValue);

  if (!zone || !labDateTime) return null;

  const utcDate = zonedTimeToUtc(labDateTime, zone.timeZone);
  return Number.isNaN(utcDate.getTime()) ? null : utcDate;
};

/** Converts a date/time entered in the browser's timezone into a UTC instant. */
export const convertBrowserDateTimeToUtc = (
  dateValue: unknown,
  timeValue?: unknown
): Date | null => {
  const browserDateTime = getLabDateTimeParts(dateValue, timeValue);
  if (!browserDateTime) return null;

  const browserTimeZone = getBrowserTimeZone();
  const utcDate = browserTimeZone
    ? zonedTimeToUtc(browserDateTime, browserTimeZone)
    : new Date(browserDateTime);

  return Number.isNaN(utcDate.getTime()) ? null : utcDate;
};

export const formatUtcTimeForApi = (utcDate: Date) =>
  formatInTimeZone(utcDate, "UTC", "HH:mm:ss");

/**
 * The API stores UTC in SQL datetime columns, which serializes without a zone.
 * Add the UTC designator explicitly so the browser never treats it as local time.
 */
const parseUtcDateTime = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== "string" || !value.trim()) return null;

  const trimmedValue = value.trim();
  const hasUsDateFormat = /^\d{1,2}[/-]\d{1,2}[/-]\d{4}/.test(trimmedValue);
  const normalizedUsDateTime = hasUsDateFormat
    ? getLabDateTimeParts(trimmedValue)
    : null;

  if (hasUsDateFormat && !normalizedUsDateTime) return null;

  const normalizedValue = (normalizedUsDateTime ?? trimmedValue).replace(
    " ",
    "T"
  );
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
  const parsedDate = new Date(
    hasZone ? normalizedValue : `${normalizedValue}Z`
  );
  if (!Number.isNaN(parsedDate.getTime())) return parsedDate;

  // Fallback ONLY for inputs the ISO parse above could not handle (e.g. the
  // "MM/DD/YYYY HH:mm:ss" format the edit API returns for DateReceived).
  // Reuse the existing normalizer, then parse as UTC. If it still cannot be
  // parsed, return null exactly as before, so no working path changes.
  const isoDateTime = getLabDateTimeParts(value);
  if (!isoDateTime) return null;
  const fallbackDate = new Date(`${isoDateTime}Z`);
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
};

const parseUtcDateAndTime = (
  dateValue: unknown,
  timeValue: unknown
): Date | null => {
  const utcDateTime = getLabDateTimeParts(dateValue, timeValue);
  return utcDateTime ? parseUtcDateTime(utcDateTime) : null;
};

/**
 * A date entered without a time is a plain calendar date. It is carried at 12:00
 * UTC, which falls on the same calendar day in every lab timezone (EST to HST)
 * and in any browser from UTC-12 to UTC+11, so it never shifts a day.
 */
export const convertDateToUtcNoon = (dateValue: unknown): Date | null =>
  parseUtcDateAndTime(dateValue, "12:00:00");

const formatCalendarDateInLabTimeZone = (
  dateValue: unknown,
  zone: NonNullable<ReturnType<typeof getLabTimeZone>>,
  dateFormat: string
): string | null => {
  const noonUtc = convertDateToUtcNoon(dateValue);

  return noonUtc
    ? `${formatInTimeZone(noonUtc, zone.timeZone, dateFormat)} (${zone.key})`
    : null;
};

const formatInBrowserTimeZone = (date: Date, format: string) => {
  const browserTimeZone = getBrowserTimeZone();
  return browserTimeZone
    ? formatInTimeZone(date, browserTimeZone, format)
    : formatLocalDate(date, format);
};

/**
 * Converts separate UTC date/time values to browser-local form values.
 * These values are intended for editable controls and deliberately omit a
 * timezone label.
 */
export const formatUtcDateTimePairInBrowserTimeZone = (
  dateValue: unknown,
  timeValue: unknown
): { date: string; time: string } | null => {
  const utcDate = parseUtcDateAndTime(dateValue, timeValue);
  if (!utcDate) return null;

  return {
    date: formatInBrowserTimeZone(utcDate, "MM/dd/yyyy"),
    time: formatInBrowserTimeZone(utcDate, "HH:mm:ss"),
  };
};

/** Converts one UTC datetime value to a browser-local editable value. */
export const formatUtcDateTimeInBrowserTimeZone = (
  value: unknown,
  format = "yyyy-MM-dd'T'HH:mm:ss"
): string | null => {
  const utcDate = parseUtcDateTime(value);
  return utcDate ? formatInBrowserTimeZone(utcDate, format) : null;
};

/** Converts one UTC datetime value to the selected lab timezone. */
export const formatUtcDateTimeInLabTimeZone = (
  value: unknown,
  labTimeZone?: string | null,
  format = "MM/dd/yyyy"
): string | null => {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value.trim())
  ) {
    return null;
  }

  const zone = getLabTimeZone(labTimeZone);
  const utcDate = parseUtcDateTime(value);

  if (!zone || !utcDate) return null;

  return `${formatInTimeZone(utcDate, zone.timeZone, format)} (${zone.key})`;
};

/**
 * Combines separate UTC date/time fields and displays the resulting instant in
 * the selected lab's timezone. Returning null lets the caller keep the API values.
 */
export const formatUtcDateTimePairInLabTimeZone = (
  dateValue: unknown,
  timeValue: unknown,
  labTimeZone?: string | null,
  dateFormat = "MM/dd/yyyy",
  timeFormat = "HH:mm:ss"
): { date: string; time: string } | null => {
  const zone = getLabTimeZone(labTimeZone);
  const utcDate = parseUtcDateAndTime(dateValue, timeValue);

  if (!zone || !utcDate) return null;

  return {
    date: `${formatInTimeZone(utcDate, zone.timeZone, dateFormat)} (${zone.key})`,
    time: `${formatInTimeZone(utcDate, zone.timeZone, timeFormat)} (${zone.key})`,
  };
};

/**
 * Collection date/time for display. A collection saved without a time shows the
 * date only and keeps its time blank; otherwise it is the usual date/time pair.
 */
export const formatCollectionDateTimePairInLabTimeZone = (
  dateValue: unknown,
  timeValue: unknown,
  labTimeZone?: string | null,
  dateFormat = "MM/dd/yyyy",
  timeFormat = "HH:mm:ss"
): { date: string; time: string } | null => {
  if (String(timeValue ?? "").trim()) {
    return formatUtcDateTimePairInLabTimeZone(
      dateValue,
      timeValue,
      labTimeZone,
      dateFormat,
      timeFormat
    );
  }

  const zone = getLabTimeZone(labTimeZone);
  const date = zone
    ? formatCalendarDateInLabTimeZone(dateValue, zone, dateFormat)
    : null;

  return date ? { date, time: "" } : null;
};

const getCollectionUtcDateTime = (
  rowData: RequisitionRow | null | undefined
): Date | null => {
  const dateValue = getRowValue(rowData, "DateofCollection");
  const timeValue = getRowValue(rowData, "TimeofCollection");

  if (typeof dateValue === "string" && typeof timeValue === "string") {
    const datePart = dateValue.trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    const timePart = timeValue
      .trim()
      .match(/^([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,7})?)?/)?.[0];

    if (datePart && timePart) {
      return parseUtcDateTime(`${datePart}T${timePart}`);
    }
  }

  return parseUtcDateTime(dateValue);
};

/**
 * Orders that come from an integration carry a ReferenceID. Their date and time
 * values have an unknown timezone, so callers must show them as received and
 * never convert them.
 */
export const isIntegrationOrder = (
  rowData: RequisitionRow | null | undefined
): boolean => String(getRowValue(rowData, "ReferenceID") ?? "").trim() !== "";

/**
 * The ReferenceID value of a row, to pass on to another page. The key is matched
 * ignoring case, exactly like isIntegrationOrder.
 */
export const getReferenceId = (
  rowData: RequisitionRow | null | undefined
): unknown => getRowValue(rowData, "ReferenceID");

/**
 * Date-range filters go to the API as the UTC window of the picked lab days. The
 * View Requisition procedure compares whole dates only and fails on a value that
 * carries a time, so only the UTC dates are sent for now. Once the procedure
 * accepts date-times, set this to true to send the exact UTC window instead.
 */
const SEND_UTC_TIME_WITH_DATE_RANGE_FILTERS = true;

const DATE_RANGE_FILTER =
  /^(\d{1,2}\/\d{1,2}\/\d{4}) to (\d{1,2}\/\d{1,2}\/\d{4})$/;

export const convertLabDateRangeToUtc = (
  filterValue: string,
  labTimeZone?: string | null
): string | null => {
  const match = filterValue.trim().match(DATE_RANGE_FILTER);
  if (!match) return null;

  const start = convertLabDateTimeToUtc(match[1], "00:00:00", labTimeZone);
  const endOfDay = convertLabDateTimeToUtc(match[2], "23:59:59", labTimeZone);
  if (!start || !endOfDay) return null;

  const end = new Date(endOfDay.getTime() + 999);

  return SEND_UTC_TIME_WITH_DATE_RANGE_FILTERS
    ? `${start.toISOString()} to ${end.toISOString()}`
    : `${formatInTimeZone(start, "UTC", "MM/dd/yyyy")} to ${formatInTimeZone(
        end,
        "UTC",
        "MM/dd/yyyy"
      )}`;
};

/**
 * Converts the lab-day date-range filters of the converted date columns to UTC,
 * for the request only. Other filters, and any value that is not a
 * "MM/dd/yyyy to MM/dd/yyyy" range, are returned unchanged.
 */
export const convertDateRangeFiltersToUtc = (
  filters: any[],
  labTimeZone?: string | null,
  extraDateColumns: string[] = []
): any[] =>
  !Array.isArray(filters)
    ? filters
    : filters.map((filter) => {
        // Some pickers (the shared PatientSearchInput date range) name the filter
        // by its column key in `columnName` and send no `columnKey`.
        const columnKey = String(
          filter?.columnKey ?? filter?.columnName ?? ""
        ).toLowerCase();

        if (
          String(filter?.columnType ?? "").toLowerCase() !== "daterange" ||
          !(
            TIME_ZONE_COLUMNS.has(columnKey) ||
            extraDateColumns.includes(columnKey)
          ) ||
          typeof filter?.filterValue !== "string"
        ) {
          return filter;
        }

        const utcRange = convertLabDateRangeToUtc(
          filter.filterValue,
          labTimeZone
        );
        return utcRange ? { ...filter, filterValue: utcRange } : filter;
      });

/**
 * Time-of-day filters (Time of Collection) are picked in the lab timezone but the
 * stored time is UTC. When this is true they are converted to UTC for the request;
 * when false they are sent as picked, exactly as before.
 *
 * Keep it false until the stored procedures handle a time range that crosses
 * midnight in UTC (e.g. 08:00-17:00 in a PST lab is 15:00-00:00 UTC): when the
 * start is later than the end they must compare with OR instead of BETWEEN.
 */
const CONVERT_TIME_FILTERS_TO_UTC = true;

const TIME_RANGE_FILTER =
  /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) to (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/;

// First day of a picked "MM/DD/YYYY to MM/DD/YYYY" collection date range. It is
// the date whose UTC offset (daylight saving) applies to the picked lab time.
const getPickedStartDate = (collectionDateRange: unknown): string | null =>
  String(collectionDateRange ?? "")
    .trim()
    .match(/^(\d{1,2}\/\d{1,2}\/\d{4}) to /)?.[1] ?? null;

/**
 * Converts a time picked in the lab timezone ("HH:mm" or "HH:mm:ss") to the same
 * clock format in UTC. The UTC offset of the first picked collection date is used
 * (today when none is picked). Returns the input unchanged when the switch is off,
 * the lab timezone is missing, or the value is not a time.
 */
export const convertLabTimeToUtc = (
  time: string,
  labTimeZone?: string | null,
  collectionDateRange?: unknown
): string => {
  const zone = getLabTimeZone(labTimeZone);
  const match = String(time ?? "")
    .trim()
    .match(/^\d{2}:\d{2}(:\d{2})?$/);

  if (!CONVERT_TIME_FILTERS_TO_UTC || !zone || !match) return time;

  const referenceDate =
    getPickedStartDate(collectionDateRange) ??
    formatInTimeZone(new Date(), zone.timeZone, "MM/dd/yyyy");
  const utcDate = convertLabDateTimeToUtc(
    referenceDate,
    time.trim(),
    labTimeZone
  );

  return utcDate
    ? formatInTimeZone(utcDate, "UTC", match[1] ? "HH:mm:ss" : "HH:mm")
    : time;
};

const convertLabTimeRangeToUtc = (
  filterValue: string,
  labTimeZone?: string | null,
  collectionDateRange?: unknown
): string | null => {
  const match = filterValue.trim().match(TIME_RANGE_FILTER);
  if (!CONVERT_TIME_FILTERS_TO_UTC || !match) return null;

  const referenceDate = getPickedStartDate(collectionDateRange) ?? match[1];
  const start = convertLabDateTimeToUtc(referenceDate, match[2], labTimeZone);
  const end = convertLabDateTimeToUtc(referenceDate, match[4], labTimeZone);
  if (!start || !end) return null;

  const clock = "yyyy-MM-dd HH:mm:ss";
  return `${formatInTimeZone(start, "UTC", clock)} to ${formatInTimeZone(
    end,
    "UTC",
    clock
  )}`;
};

const filterRefersTo = (filter: any, columnName: string) =>
  [filter?.columnKey, filter?.columnName].some((name) =>
    String(name ?? "")
      .toLowerCase()
      .endsWith(columnName)
  );

/**
 * Everything convertDateRangeFiltersToUtc does, plus the Time of Collection range
 * filter (only when CONVERT_TIME_FILTERS_TO_UTC is on). For the request only.
 */
export const convertRangeFiltersToUtc = (
  filters: any[],
  labTimeZone?: string | null,
  extraDateColumns: string[] = []
): any[] => {
  if (!Array.isArray(filters)) return filters;

  const collectionDateRange = filters.find(
    (filter) =>
      String(filter?.columnType ?? "").toLowerCase() === "daterange" &&
      filterRefersTo(filter, "dateofcollection")
  )?.filterValue;

  return convertDateRangeFiltersToUtc(
    filters,
    labTimeZone,
    extraDateColumns
  ).map((converted, index) => {
    const original = filters[index];

    if (
      String(original?.columnType ?? "").toLowerCase() !== "timerange" ||
      !filterRefersTo(original, "timeofcollection") ||
      typeof original?.filterValue !== "string"
    ) {
      return converted;
    }

    const utcRange = convertLabTimeRangeToUtc(
      original.filterValue,
      labTimeZone,
      collectionDateRange
    );
    return utcRange ? { ...original, filterValue: utcRange } : converted;
  });
};

/**
 * Formats only the three View Requisition UTC columns in the selected lab's
 * timezone. `undefined` tells the caller to retain its existing formatter.
 */
export const formatViewRequisitionLabTime = (
  rowData: RequisitionRow,
  columnKey: string,
  labTimeZone?: string | null
): string | undefined => {
  const normalizedColumnKey = columnKey?.toLowerCase();

  if (!TIME_ZONE_COLUMNS.has(normalizedColumnKey)) return undefined;

  const zone = getLabTimeZone(labTimeZone);
  if (!zone) return undefined;

  const columnValue = getRowValue(rowData, columnKey);
  if (!columnValue) return "";

  // A collection date saved without a time (optional for some labs) is a plain
  // calendar date carried at 12:00 UTC: show its day and never invent a time.
  if (
    normalizedColumnKey === "dateofcollection" &&
    rowData &&
    Object.keys(rowData).some(
      (key) => key.toLowerCase() === "timeofcollection"
    ) &&
    !String(getRowValue(rowData, "TimeofCollection") ?? "").trim()
  ) {
    return (
      formatCalendarDateInLabTimeZone(columnValue, zone, "MM/dd/yyyy") ??
      undefined
    );
  }

  const utcDate =
    normalizedColumnKey === "dateofcollection" ||
    normalizedColumnKey === "timeofcollection"
      ? getCollectionUtcDateTime(rowData)
      : parseUtcDateTime(columnValue);

  if (!utcDate) return undefined;

  const format =
    normalizedColumnKey === "timeofcollection" ? "hh:mm:ss a" : "MM/dd/yyyy";

  return `${formatInTimeZone(utcDate, zone.timeZone, format)} (${zone.key})`;
};
